import React, { useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImagePlus, Trash2 } from "lucide-react";
import { Modal } from "../../ui/Modal";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import { Switch } from "../../ui/Switch";
import { SearchableSelect } from "../../ui/SearchableSelect";
import {
  ANNOUNCEMENT_IMAGE_TYPES,
  createAnnouncement,
  previewAnnouncementAudience,
  publishAnnouncement,
  updateAnnouncement,
  uploadAnnouncementImage,
} from "../../../api/announcements";
import AnnouncementPreview from "./AnnouncementPreview";
import AudienceEditor from "./AudienceEditor";
import MarkdownEditor from "./MarkdownEditor";
import {
  audiencePayload,
  emptyForm,
  formFromAnnouncement,
  LIMITS,
  ownerParams,
  toPayload,
  validateForm,
} from "./announcementForm";

// ─────────────────────────────────────────────────────────────────────────────
// Crear / editar un pop-up. Formulario a la izquierda y, a la derecha, la
// vista previa con el mismo componente que verá el comercio.
//
// Publicar pide confirmación con el alcance real de HOY (lo calcula el
// servidor): es la última oportunidad de ver que la audiencia no quedó vacía
// o más grande de lo pensado.
// ─────────────────────────────────────────────────────────────────────────────

function Field({ label, htmlFor, error, hint, children, counter }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={htmlFor} className="text-sm font-medium text-fg">
          {label}
        </label>
        {counter}
      </div>
      {children}
      {error ? (
        <span className="text-xs text-status-error">{error}</span>
      ) : hint ? (
        <span className="text-xs text-fg-muted">{hint}</span>
      ) : null}
    </div>
  );
}

function Counter({ value, max }) {
  return (
    <span className="text-xs tabular-nums text-fg-muted">
      {value.length}/{max}
    </span>
  );
}

function SenderChoice({ value, onChange, t }) {
  const options = [
    { value: "platform", label: t("Beartrack") },
    { value: "operator", label: t("A nombre de un operador") },
  ];
  return (
    <div role="radiogroup" aria-label={t("Remitente")} className="inline-flex rounded-lg border border-border p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          onClick={() => onChange(option.value)}
          className={
            value === option.value
              ? "rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white"
              : "rounded-md px-3 py-1.5 text-sm font-medium text-fg-muted hover:bg-bg-muted"
          }
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onSaved: () => void,
 *   announcement: object|null,   // null = nuevo
 *   systemMode: boolean,
 *   operators: {id: string, name: string, logo?: string|null}[],
 *   ownOperator?: {id: string, name: string, logo?: string|null},
 *   operatorCountryCode: string,
 * }} props
 */
export default function AnnouncementEditorModal({
  open,
  onClose,
  onSaved,
  announcement,
  systemMode,
  operators,
  ownOperator,
  operatorCountryCode,
}) {
  const { t } = useTranslation();
  const ids = { title: useId(), body: useId(), image: useId(), ctaLabel: useId(), ctaUrl: useId(), startsAt: useId(), endsAt: useId() };
  const imageRef = useRef(null);

  const [current, setCurrent] = useState(announcement);
  const isNew = !current;
  const [form, setForm] = useState(() =>
    announcement ? formFromAnnouncement(announcement) : emptyForm({ systemMode }),
  );
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [busy, setBusy] = useState(false);
  const [imageState, setImageState] = useState({ uploading: false, error: "" });
  const [confirm, setConfirm] = useState(null); // { total } antes de publicar

  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setConfirm(null);
  };
  const setAudience = (patch) => {
    setForm((f) => ({ ...f, audience: { ...f.audience, ...patch } }));
    setConfirm(null);
  };

  const operatorOptions = useMemo(
    () => operators.map((o) => ({ value: o.id, label: o.name })),
    [operators],
  );

  const sender = useMemo(() => {
    if (systemMode && form.scope === "platform") return { kind: "platform" };
    const operator = systemMode
      ? operators.find((o) => o.id === form.logisticsCompanyId)
      : ownOperator;
    return { kind: "operator", name: operator?.name ?? null, logo: operator?.logo ?? null };
  }, [systemMode, form.scope, form.logisticsCompanyId, operators, ownOperator]);

  const ownerKey = systemMode
    ? form.scope === "platform"
      ? "platform"
      : form.logisticsCompanyId || "platform"
    : ownOperator?.id || "operator";

  const status = current?.status ?? "draft";
  const isPublished = status === "published";

  const onPickImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setImageState({ uploading: true, error: "" });
    const result = await uploadAnnouncementImage(file, ownerKey);
    setImageState({ uploading: false, error: result.ok ? "" : t(result.error) });
    if (result.ok) set({ imageUrl: result.url });
  };

  const validate = () => {
    const next = validateForm(form, { systemMode, isNew });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  /** Crea o actualiza; devuelve el pop-up guardado o null. */
  const save = async () => {
    const payload = toPayload(form, { systemMode, isNew });
    const result = isNew
      ? await createAnnouncement(payload)
      : await updateAnnouncement(current.id, payload);
    if (!result.ok) {
      setServerError(t(result.error));
      return null;
    }
    setCurrent(result.data);
    return result.data;
  };

  const onSaveDraft = async () => {
    setServerError("");
    if (!validate()) return;
    setBusy(true);
    const saved = await save();
    setBusy(false);
    if (saved) {
      onSaved();
      onClose();
    }
  };

  const onRequestPublish = async () => {
    setServerError("");
    if (!validate()) return;
    setBusy(true);
    const result = await previewAnnouncementAudience({
      ...ownerParams(form, { systemMode }),
      audience: audiencePayload(form),
    });
    setBusy(false);
    if (!result.ok) {
      setServerError(t(result.error));
      return;
    }
    setConfirm({ total: result.data?.total ?? 0 });
  };

  const onConfirmPublish = async () => {
    setServerError("");
    setBusy(true);
    const saved = await save();
    if (!saved) {
      setBusy(false);
      return;
    }
    const published = await publishAnnouncement(saved.id);
    setBusy(false);
    if (!published.ok) {
      // Quedó guardado como borrador: se informa y el modal sigue abierto.
      setServerError(t(published.error));
      onSaved();
      return;
    }
    onSaved();
    onClose();
  };

  const footer = confirm ? (
    <div className="flex w-full flex-wrap items-center gap-3">
      <p className="mr-auto text-sm text-fg">
        {confirm.total === 0
          ? t("Hoy no llega a ningún comercio. ¿Publicarlo igual?")
          : t("Se mostrará a {{n}} comercios al iniciar sesión. ¿Publicar?", { n: confirm.total })}
      </p>
      <Button variant="ghost" onClick={() => setConfirm(null)} disabled={busy}>
        {t("Volver")}
      </Button>
      <Button onClick={onConfirmPublish} loading={busy}>
        {t("Publicar")}
      </Button>
    </div>
  ) : (
    <div className="flex w-full flex-wrap items-center justify-end gap-3">
      <Button variant="ghost" onClick={onClose} disabled={busy}>
        {t("Cancelar")}
      </Button>
      {isPublished ? (
        <Button onClick={onSaveDraft} loading={busy}>
          {t("Guardar cambios")}
        </Button>
      ) : (
        <>
          <Button variant="outline" onClick={onSaveDraft} loading={busy}>
            {t("Guardar borrador")}
          </Button>
          <Button onClick={onRequestPublish} loading={busy}>
            {t("Publicar")}
          </Button>
        </>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={busy ? undefined : onClose}
      size="xl"
      title={isNew ? t("Nuevo pop-up") : t("Editar pop-up")}
      description={
        isPublished
          ? t("Está publicado: los cambios los ve quien todavía no lo descartó.")
          : t("Se muestra a los comercios al iniciar sesión en el portal.")
      }
      footer={footer}
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-5">
          {serverError && (
            <div role="alert" className="rounded-lg bg-status-error-soft px-3 py-2 text-sm text-status-error">
              {serverError}
            </div>
          )}

          {systemMode && (
            <Field label={t("Remitente")} error={errors.logisticsCompanyId && t(errors.logisticsCompanyId)}>
              {isNew ? (
                <div className="flex flex-col gap-2">
                  <SenderChoice
                    t={t}
                    value={form.scope}
                    onChange={(scope) =>
                      set({ scope, audience: { ...form.audience, operatorIds: [], integrations: [] } })
                    }
                  />
                  {form.scope === "operator" && (
                    <SearchableSelect
                      options={operatorOptions}
                      value={form.logisticsCompanyId}
                      onChange={(value) => set({ logisticsCompanyId: value || "" })}
                      placeholder={t("Elige el operador")}
                      aria-label={t("Operador")}
                    />
                  )}
                </div>
              ) : (
                <p className="text-sm text-fg">
                  {form.scope === "platform" ? t("Beartrack") : sender.name || t("Operador")}
                </p>
              )}
            </Field>
          )}

          <Field
            label={t("Título")}
            htmlFor={ids.title}
            error={errors.title && t(errors.title)}
            counter={<Counter value={form.title} max={LIMITS.title} />}
          >
            <Input
              id={ids.title}
              value={form.title}
              maxLength={LIMITS.title}
              onChange={(e) => set({ title: e.target.value })}
              invalid={Boolean(errors.title)}
            />
          </Field>

          <Field
            label={t("Imagen principal (opcional)")}
            htmlFor={ids.image}
            error={imageState.error}
            hint={t("PNG, JPG, WEBP o GIF de hasta 2 MB.")}
          >
            {form.imageUrl ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                <img
                  src={form.imageUrl}
                  alt=""
                  className="dark-safe-img h-24 w-40 rounded-lg border border-border object-cover"
                />
                <div className="flex flex-1 flex-col gap-2">
                  <Input
                    id={ids.image}
                    value={form.imageAlt}
                    maxLength={LIMITS.imageAlt}
                    onChange={(e) => set({ imageAlt: e.target.value })}
                    placeholder={t("Describe la imagen (para lectores de pantalla)")}
                  />
                  <div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => set({ imageUrl: "", imageAlt: "" })}
                      iconLeft={<Trash2 className="h-4 w-4" aria-hidden="true" />}
                    >
                      {t("Quitar imagen")}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <Button
                  id={ids.image}
                  size="sm"
                  variant="outline"
                  onClick={() => imageRef.current?.click()}
                  loading={imageState.uploading}
                  iconLeft={<ImagePlus className="h-4 w-4" aria-hidden="true" />}
                >
                  {t("Subir imagen")}
                </Button>
              </div>
            )}
            <input
              ref={imageRef}
              type="file"
              accept={Object.keys(ANNOUNCEMENT_IMAGE_TYPES).join(",")}
              className="hidden"
              onChange={onPickImage}
              aria-hidden="true"
              tabIndex={-1}
            />
          </Field>

          <Field label={t("Mensaje")} htmlFor={ids.body} error={errors.body && t(errors.body)}>
            <MarkdownEditor
              id={ids.body}
              value={form.body}
              onChange={(body) => set({ body })}
              ownerKey={ownerKey}
              invalid={Boolean(errors.body)}
            />
          </Field>

          <div className="flex flex-col gap-3">
            <Switch
              checked={form.ctaEnabled}
              onChange={(ctaEnabled) => set({ ctaEnabled })}
              label={t("Agregar un botón con link")}
            />
            {form.ctaEnabled && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field
                  label={t("Texto del botón")}
                  htmlFor={ids.ctaLabel}
                  error={errors.ctaLabel && t(errors.ctaLabel)}
                  counter={<Counter value={form.ctaLabel} max={LIMITS.ctaLabel} />}
                >
                  <Input
                    id={ids.ctaLabel}
                    value={form.ctaLabel}
                    maxLength={LIMITS.ctaLabel}
                    onChange={(e) => set({ ctaLabel: e.target.value })}
                    invalid={Boolean(errors.ctaLabel)}
                  />
                </Field>
                <Field
                  label={t("Link del botón")}
                  htmlFor={ids.ctaUrl}
                  error={errors.ctaUrl && t(errors.ctaUrl)}
                  hint={t("https://, mailto: o tel:")}
                >
                  <Input
                    id={ids.ctaUrl}
                    value={form.ctaUrl}
                    maxLength={LIMITS.url}
                    onChange={(e) => set({ ctaUrl: e.target.value })}
                    placeholder="https://"
                    invalid={Boolean(errors.ctaUrl)}
                  />
                </Field>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t("Se muestra desde")} htmlFor={ids.startsAt} error={errors.startsAt && t(errors.startsAt)}>
              <Input
                id={ids.startsAt}
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => set({ startsAt: e.target.value })}
                invalid={Boolean(errors.startsAt)}
              />
            </Field>
            <Field
              label={t("Hasta (opcional)")}
              htmlFor={ids.endsAt}
              error={errors.endsAt && t(errors.endsAt)}
              hint={t("Sin fecha, se muestra hasta que lo archives.")}
            >
              <Input
                id={ids.endsAt}
                type="datetime-local"
                value={form.endsAt}
                onChange={(e) => set({ endsAt: e.target.value })}
                invalid={Boolean(errors.endsAt)}
              />
            </Field>
          </div>

          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-medium text-fg">{t("Audiencia")}</h3>
            <AudienceEditor
              form={form}
              onAudienceChange={setAudience}
              systemMode={systemMode}
              operators={operators}
              operatorCountryCode={operatorCountryCode}
              error={errors.audience}
            />
          </div>
        </div>

        <div className="lg:sticky lg:top-0 lg:self-start">
          <AnnouncementPreview
            title={form.title}
            body={form.body}
            imageUrl={form.imageUrl}
            imageAlt={form.imageAlt}
            ctaLabel={form.ctaEnabled ? form.ctaLabel : ""}
            sender={sender}
          />
        </div>
      </div>
    </Modal>
  );
}
